import React, { useState, useMemo } from 'react';
import { Hammer, Receipt, DollarSign } from 'lucide-react';
import { Card } from '../ui';
import { Input } from '../ui';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981'];

export function GuildRateCardBuilder() {
  const [laborRate, setLaborRate] = useState<number>(45);
  const [estimatedHours, setEstimatedHours] = useState<number>(40);
  const [materialsCost, setMaterialsCost] = useState<number>(1200);
  const [overheadPercent, setOverheadPercent] = useState<number>(15);
  const [targetMargin, setTargetMargin] = useState<number>(20);

  const model = useMemo(() => {
    const rawLaborCost = laborRate * estimatedHours;
    const directCosts = rawLaborCost + materialsCost;
    
    // Overhead is calculated as a percentage of direct costs
    const overheadCost = directCosts * (overheadPercent / 100);
    const totalCost = directCosts + overheadCost;

    // To hit a target margin of M%, Price = TotalCost / (1 - M)
    // Example: 20% margin => Price = Cost / 0.8
    const requiredPrice = targetMargin >= 100 ? totalCost * 2 : totalCost / (1 - (targetMargin / 100));
    
    const profit = requiredPrice - totalCost;

    const breakdown = [
      { name: 'Labor Cost', value: rawLaborCost, color: COLORS[0] },
      { name: 'Materials', value: materialsCost, color: COLORS[1] },
      { name: 'Overhead', value: overheadCost, color: COLORS[2] },
      { name: 'Net Profit', value: profit, color: COLORS[3] }
    ];

    return {
      rawLaborCost,
      directCosts,
      overheadCost,
      totalCost,
      requiredPrice,
      profit,
      breakdown
    };
  }, [laborRate, estimatedHours, materialsCost, overheadPercent, targetMargin]);

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  return (
    <div className="max-w-5xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-blue-700 to-indigo-800 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Hammer className="w-6 h-6 mr-3 text-blue-300" />
            Project Rate Card Builder
          </h2>
          <p className="text-blue-100 max-w-xl">
            Never underbid a job again. Accurately calculate project estimates by factoring in direct labor, materials, organizational overhead, and your target profit margin.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        
        {/* Controls */}
        <Card className="p-6 space-y-6 md:col-span-1 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
          <div>
            <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Labor Rate ($/hr)</label>
            <Input 
              type="number" 
              value={laborRate} 
              onChange={(e) => setLaborRate(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Estimated Hours</label>
            <Input 
              type="number" 
              value={estimatedHours} 
              onChange={(e) => setEstimatedHours(Number(e.target.value))}
            />
          </div>
          <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
            <label className="text-sm font-semibold text-slate-500 uppercase block mb-2">Materials Cost ($)</label>
            <Input 
              type="number" 
              value={materialsCost} 
              onChange={(e) => setMaterialsCost(Number(e.target.value))}
            />
          </div>

          <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
            <label className="text-sm font-semibold text-slate-500 uppercase flex justify-between mb-2">
              <span>Overhead</span>
              <span className="font-bold text-slate-900 dark:text-white">{overheadPercent}%</span>
            </label>
            <input 
              type="range" 
              min="0" 
              max="50" 
              value={overheadPercent} 
              onChange={(e) => setOverheadPercent(Number(e.target.value))}
              className="w-full accent-blue-600"
            />
            <p className="text-xs text-slate-400 mt-2">Covers admin, insurance, and facility costs.</p>
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-500 uppercase flex justify-between mb-2">
              <span className="text-emerald-600 dark:text-emerald-500">Target Profit Margin</span>
              <span className="font-bold text-emerald-700 dark:text-emerald-400">{targetMargin}%</span>
            </label>
            <input 
              type="range" 
              min="0" 
              max="60" 
              value={targetMargin} 
              onChange={(e) => setTargetMargin(Number(e.target.value))}
              className="w-full accent-emerald-500"
            />
          </div>
        </Card>

        {/* Results */}
        <div className="md:col-span-2 space-y-6">
          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-slate-900 text-white flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center">
                <Receipt className="w-4 h-4 mr-2" /> Recommended Bid Price
              </h3>
              <div className="text-5xl font-black text-white tracking-tight">
                {formatCurrency(model.requiredPrice)}
              </div>
            </div>
            
            <div className="flex flex-col items-end">
              <div className="text-sm text-slate-400 uppercase tracking-wider font-bold mb-1">Net Profit</div>
              <div className="text-3xl font-black text-emerald-400">
                {formatCurrency(model.profit)}
              </div>
            </div>
          </Card>

          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <h3 className="text-lg font-bold mb-6 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-blue-500" /> Revenue Breakdown
            </h3>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="h-[250px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={model.breakdown}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                    >
                      {model.breakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: any) => formatCurrency(Number(value))}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Legend verticalAlign="bottom" height={36}/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-4">
                {model.breakdown.map((item) => (
                  <div key={item.name} className="flex justify-between items-center p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div className="font-bold text-sm" style={{ color: item.color }}>{item.name}</div>
                    <div className="font-bold">{formatCurrency(item.value)}</div>
                  </div>
                ))}
                <div className="flex justify-between items-center p-3 rounded-lg bg-slate-100 dark:bg-slate-800 mt-4 border-t border-slate-200 dark:border-slate-700">
                  <div className="font-bold text-sm text-slate-500 uppercase">Total Costs</div>
                  <div className="font-bold text-slate-900 dark:text-white">{formatCurrency(model.totalCost)}</div>
                </div>
              </div>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
